import type { Product, Discount, Media as MediaType } from '@/payload-types'

import { Link } from '@/i18n/routing'
import React from 'react'
import { Media } from '@/components/Media'
import { Price } from '@/components/Price'
import {
  ProductBanner,
  VariantBanner,
  BulkDiscountBanner,
  DiscountRibbon,
} from '@/components/product/ProductBanners'
import { getProductDiscountInfo, calculateDiscountedPrice } from '@/lib/productDiscounts'
import { GlassSurface } from '@/components/ui/glass'
import { WishlistButton } from '@/components/WishlistButton'

type Props = {
  product: Partial<Product>
  activeDiscounts?: Discount[]
  isLoggedIn?: boolean
  locale?: string
  priority?: boolean
  /** data-testid — the shop grid and the featured carousel share this card */
  testId?: string
}

/*
 * The one product card (Phase 7e, 2026-09-26). Peeled-back treatment: a
 * blur-less `GlassSurface` (a shop page renders ~12 of these, so a
 * backdrop-filter here alone would blow the runtime budget), tokenised colours,
 * no "View" cart pill — the whole card is the link. Used by the shop grid,
 * category pages, product-detail related items and (via FeaturedProductCard)
 * the home page carousel.
 */
export const ProductGridItem: React.FC<Props> = ({
  product,
  activeDiscounts,
  isLoggedIn,
  locale,
  priority,
  testId = 'product-grid-item',
}) => {
  const { gallery, basePrice, title, variants } = product

  const price = basePrice
  const hasVariants = Array.isArray(variants) && variants.length > 0

  let lowestAmount = 0
  let highestAmount = 0
  if (hasVariants) {
    const allPrices = variants.flatMap(
      (v) =>
        v.values?.map((val) => val.price).filter((p): p is number => typeof p === 'number') || [],
    )
    if (allPrices.length > 0) {
      lowestAmount = Math.min(...allPrices)
      highestAmount = Math.max(...allPrices)
    }
  }

  const discountInfo = getProductDiscountInfo(product, activeDiscounts)

  const metaImage =
    product.meta?.image && typeof product.meta.image !== 'string' ? product.meta.image : undefined
  const galleryImage =
    Array.isArray(gallery) && gallery.length > 0 && typeof gallery[0]?.image === 'object'
      ? (gallery[0].image as MediaType)
      : undefined
  const image = metaImage || galleryImage || false

  return (
    <Link
      data-testid={testId}
      data-product-id={product.id}
      className="relative flex flex-col h-full w-full group transition-all duration-300 ease-out hover:-translate-y-1.5"
      href={`/products/${product.slug}`}
    >
      <GlassSurface
        variant="card"
        elevation="none"
        interactive
        blur={false}
        className="h-full rounded-2xl transition-all duration-300"
        style={{ borderRadius: '1rem' }}
      >
        <div className="p-3">
          {image ? (
            <div className="relative aspect-square">
              <Media
                className="relative aspect-square object-cover rounded-xl bg-transparent"
                height={80}
                imgClassName="h-full w-full object-cover rounded-xl transition duration-300 ease-in-out group-hover:scale-105"
                priority={priority}
                resource={image}
                size="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
                width={80}
              />
              <WishlistButton
                productId={String(product.id)}
                variant="icon"
                isLoggedIn={isLoggedIn}
                loginHref={locale ? `/${locale}/login` : undefined}
              />
            </div>
          ) : (
            <div className="relative aspect-square rounded-xl bg-foreground/[0.04] flex items-center justify-center">
              <div className="text-muted-foreground text-4xl">📦</div>
              <WishlistButton
                productId={String(product.id)}
                variant="icon"
                isLoggedIn={isLoggedIn}
                loginHref={locale ? `/${locale}/login` : undefined}
              />
            </div>
          )}

          <ProductBanner>
            {discountInfo.hasVariants && <VariantBanner />}
            {discountInfo.hasBulkDiscount && <BulkDiscountBanner />}
          </ProductBanner>

          {discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage && (
            <DiscountRibbon discountPercentage={discountInfo.manualDiscountPercentage} />
          )}
        </div>

        <div className="relative flex flex-col items-center text-center gap-1.5 px-3 pt-3 pb-3.5 border-t border-border bg-transparent transition-colors duration-300">
          <div className="text-foreground font-semibold text-sm tracking-wide leading-snug px-1">
            {title}
          </div>

          {typeof price === 'number' && price > 0 ? (
            (() => {
              const hasDiscount =
                discountInfo.hasManualDiscount && discountInfo.manualDiscountPercentage
              const discountedPrice = hasDiscount
                ? calculateDiscountedPrice(price, discountInfo.manualDiscountPercentage!)
                : price

              return (
                <div className="text-emerald-700 dark:text-emerald-400 font-bold text-base">
                  <Price amount={discountedPrice} originalAmount={hasDiscount ? price : undefined} />
                </div>
              )
            })()
          ) : hasVariants && lowestAmount > 0 ? (
            <div className="text-emerald-700 dark:text-emerald-400 font-bold text-base">
              {highestAmount > lowestAmount ? (
                <Price lowestAmount={lowestAmount} highestAmount={highestAmount} />
              ) : (
                <Price amount={lowestAmount} />
              )}
            </div>
          ) : (
            <div className="text-muted-foreground text-xs">Price not available</div>
          )}
        </div>
      </GlassSurface>
    </Link>
  )
}
